// 도로 셀 — 도로가 지나는 지형 셀만 서브셀로 잘게 쪼개기 위한 전역 셀 집합.
//
// [무엇을 하나] `world_road` 폴리곤이 덮는 8m 셀을 Set 으로 만들어 두고,
//   렌더(hfBuild.getCell)와 발밑(heightfieldData.hfSurfaceYAt)이 **같은 Set 하나**를 본다.
//   그 셀에서만 상판 분할 s 를 「도로서브셀」 슬라이더 값으로 올린다.
//
// [왜 필요한가] 복셀 상판은 셀 하나가 평면 하나다. 그래서 밴드를 완전히 풀어도(평탄화)
//   경사 지형은 8m 마다 «경사×8m» 짜리 단이 남는다 — 후암 마을 경사 0.09~0.16 ⇒ 0.7~1.3m.
//   답판을 좁히는 것만이 이 단을 줄인다(단 ∝ 1/s). 실측은
//   `node front/scripts/road_voxel_tear_probe.mjs`, 눈으로 보는 건 /tech `lab-voxel-road-tear`.
//
// [왜 도로만인가] 「복셀서브셀」(hfVoxSub)은 전 지형이라 s² 로 삼각형이 늘어 함부로 못 올린다.
//   도로는 후암동 기준 8m 셀 6,079개뿐이고, 정작 «끊겨 보이면 안 되는» 곳도 거기다.
//
// [왜 parcelGrade 와 같은 모양인가] 「셀 Set 을 렌더와 발밑이 공유한다」는 문제가 완전히 같다.
//   한쪽만 고치면 캐릭터가 지형에 묻히거나 뜬다 — 이 프로젝트가 세 번 겪은 사고(SOP §5-C).
//   그래서 새 개념을 만들지 않고 그 파일의 구조를 그대로 따른다.
//
// [래스터화 규칙 — parcelGrade 와 반대다] 필지는 셀 **중심**이 안에 들어야 채택한다(밖으로
//   삐져나가면 안 되니까). 도로는 폭 1.2~12m 로 셀(8m)보다 좁은 게 대부분이라 중심 판정으로는
//   길이 통째로 누락된다. 그래서 **폴리곤 둘레를 걸으며 닿는 셀을 전부** 채택한다.
//   얇은 리본이라 둘레만 걸어도 내부가 다 덮인다(12m 도로가 8m 셀을 완전히 품으려면 양방향
//   16m 가 필요한데 그런 도로가 없다). 넉넉히 잡히는 쪽 오차는 삼각형 몇 개일 뿐이라 안전하다.
import { VOXEL_GRID_OFF, globalCellIdx } from './hfConstants';
import { worldToCellSub, cellKey } from './terrainEdits';
// 사전 굽기 로더 — `roadKindAt`/`roadMaskFill` 의 «빠른 길»이다. 못 답하면 -1 을 주고
// 여기 폴리곤 경로가 그대로 산다(`?roadbake=0` 이면 항상 -1). 자세한 계약은 그 파일 머리주석.
// ⚠ 방향이 한쪽이다 — roadBake 는 이 파일도, heightfieldData 도 import 하지 않는다(순환 방지).
import { roadBakeKindAt, roadBakeFill, setRoadBakeMeta, setRoadBakeAnchor, onRoadBakeLand } from './roadBake';

// ── 저장 단위가 「월드 전체 Set 하나」가 아니라 「렌더 타일 한 장」이다 ──
//
// [옛 구조와 그 함정] 도로 셀을 월드 전체 Set 하나에 담고 버전(_ver) 하나를 올렸다.
//   `roadCellsVersion()` 이 HeightfieldPoC 의 `stepKeyBase` 에 실려 있어서, 어디 한 군데만
//   바뀌어도 **활성 타일 전부의 재빌드 키가 바뀌어 전량 재메시**됐다. 그리고 걸어 다니면
//   `roadsReady` 가 계속 커지므로(동이 스트리밍될 때마다) 그 「한 군데」가 쉬지 않고 바뀐다.
//   게다가 상한 6만을 넘기면 잘라내는데, 새 동이 오면 **살아남는 6만의 구성이 달라져서**
//   내용이 실제로 안 바뀐 지역까지 버전이 흔들렸다. 실측: 격자 밀집구역에서 153,308셀,
//   43초 녹화에서 이 파일의 setTimeout 이 긴태스크 누적 **426ms**(3위).
//
// [지금 구조] `Map<"tx_tz", Set<cellKey>>` + **타일마다 따로 서명(sig)**.
//   · 타일 = 렌더 타일과 같은 128셀(1024m) 격자다. 한 타일의 빌드는 자기 셀 범위
//     [tx*128, tx*128+127] 밖을 절대 조회하지 않으므로 버킷 경계가 정확히 맞는다.
//   · 새 동이 들어오면 **그 동이 걸치는 타일의 sig 만** 바뀐다 → 이미 지어 둔 타일은 그대로.
//   · 타일 하나는 최대 128×128=16,384셀이라 **상한도 정렬도 필요 없다**. 옛 코드의
//     `[...set].sort().slice()` (8만~15만 원소 정렬)가 통째로 사라졌다.
//   · 도로가 잘리지 않으므로 「상한 넘어 잘랐음」 배너의 원인 자체가 없어진다.
//
// [메모리] 대신 상주량이 는다(옛 6만 → 실측 15만). 그래서 **보관 범위**를 정한다.
//   ⚠ 「총량 상한을 넘으면 먼 것부터 버린다」로 처음 짰다가 **바로 되물렀다**.
//     실측 상주가 119,486 인데 상한을 120,000 으로 잡아서, 경계에서 타일이 들락날락하며
//     sig 가 0↔실값으로 흔들린다 → 그 타일이 매번 재메시된다 → 지오메트리가 계속 새로
//     만들어진다. **고치려던 「전량 재메시」를 더 나쁜 형태로 되살리는 설계**였다.
//     (실제로 그 직후 PC 에서 `context-lost` 배너가 떴다. 인과는 미확정이지만 이 설계는
//      그것과 무관하게 틀렸다 — 상한을 올려도 언젠가 같은 자리에 선다.)
//   지금은 **개수가 아니라 거리**로 자른다. 도로 셀이 쓸모 있는 곳은 「지형 타일이 살아 있는
//   범위」뿐이고(heightfieldData 의 ACTIVE 2048m / KEEP 2432m), 그 밖은 애초에 안 지어진다.
//   앵커(카메라 타일)에서 KEEP_TILES 칸 밖은 **래스터화도 안 한다** — 버리는 게 아니라
//   처음부터 안 만든다. 앵커는 타일 격자(1024m) 단위로만 움직이므로 히스테리시스가 공짜다.
const TILE = 128;            // ⚠ HeightfieldPoC.TILE · heightfieldData.HF_TILE 과 **같은 값**이어야 한다
// 앵커에서 이 칸 수 밖의 타일은 만들지 않는다. 3칸 = 3,072m 로 heightfieldData 의 KEEP(2,432m)
// 을 덮는다 — 지형 타일이 살아 있는 범위보다 넓으므로 「도로만 없는 지형」이 생기지 않는다.
const KEEP_TILES = 3;

let _rawRings = null; // 마지막으로 받은 배열 자체 — 같은 참조면 래스터화를 건너뛰는 가드용
let _rings = [];      // 월드 좌표 링 [[ [x,z], ... ], ...] — 호출자가 lng/lat 을 변환해 넘긴다
let _grid = null;     // { cell, ox, oz }
let _tiles = new Map();  // "tx_tz" → Set<"cgx_cgz">
let _sigs = new Map();   // "tx_tz" → number   ← **이게 타일별 재빌드 키다**
let _anchor = null;      // 카메라 타일 [tx,tz] — 이 둘레 KEEP_TILES 칸만 만든다. null 이면 전부
let _ver = 0;            // 전역 버전 — 구독자 통지·소품 색 캐시용. **재빌드 키에는 안 쓴다.**
const listeners = new Set();

const cellHash = (k) => {
  let h = 0;
  for (let i = 0; i < k.length; i += 1) h = (Math.imul(h, 31) + k.charCodeAt(i)) | 0;
  return h;
};

// 타일 서명 — 집합이 같으면 항상 같은 수가 나와야 한다(순서 무관). 합은 교환법칙이 성립하고,
// 크기를 XOR 로 섞어 「합이 우연히 같은 다른 집합」을 가른다.
const sigOf = (set) => {
  let h = 0;
  for (const k of set) h = (h + cellHash(k)) | 0;
  return (h ^ (set.size * 0x9e3779b1)) | 0;
};

const tileKey = (i, j) => `${Math.floor(i / TILE)}_${Math.floor(j / TILE)}`;

const rasterize = () => {
  if (!_grid || !_rings.length) return null;
  const { cell, ox, oz } = _grid;
  // globalCellIdx 와 **같은 식** — 전역 셀 인덱스 = round(원점/셀) + 로컬 인덱스.
  const baseI = Math.round(ox / cell), baseJ = Math.round(oz / cell);
  const out = new Map();
  let total = 0;
  // parcelGrade.toIx 와 **같은 식** — 셀 경계가 VOXEL_GRID_OFF 만큼 밀려 있다.
  const toIx = (v, o) => Math.floor((v - VOXEL_GRID_OFF - o) / cell);
  const mark = (x, z) => {
    const i = toIx(x, ox), j = toIx(z, oz);
    const tx = Math.floor(i / TILE), tz = Math.floor(j / TILE);
    // 앵커 밖이면 **만들지 않는다.** 버리는 게 아니라 안 만드는 것이라 sig 가 흔들릴 여지가 없다.
    if (_anchor && (Math.abs(tx - _anchor[0]) > KEEP_TILES || Math.abs(tz - _anchor[1]) > KEEP_TILES)) return;
    const tk = `${tx}_${tz}`;
    let set = out.get(tk);
    if (!set) { set = new Set(); out.set(tk, set); }
    const k = cellKey(baseI + i, baseJ + j);
    if (!set.has(k)) { set.add(k); total += 1; }
  };
  const STEP = cell / 4;   // 2m — 셀 한 칸을 건너뛰지 않을 만큼 촘촘하면 충분하다
  for (const ring of _rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
      const [ax, az] = ring[j], [bx, bz] = ring[i];
      const len = Math.hypot(bx - ax, bz - az);
      const n = Math.max(1, Math.ceil(len / STEP));
      for (let t = 0; t <= n; t += 1) mark(ax + ((bx - ax) * t) / n, az + ((bz - az) * t) / n);
    }
  }
  return out.size ? out : null;
};

/** 카메라가 선 타일을 알려준다 — 이 둘레 KEEP_TILES 칸만 도로 셀을 만든다.
 *  ⚠ 타일이 바뀔 때만 다시 계산한다. 매 프레임 부르면 스로틀이 통째로 무력해진다. */
export const setRoadAnchor = (tx, tz) => {
  // 구운 도로 타일도 **같은 격자·같은 앵커**를 쓴다(둘 다 128셀 1,024m).
  // ⚠ **「여기가 유일한 앵커 통지 지점」이라고 적혀 있었는데 틀렸다**.
  //   이 함수의 호출부는 `HeightfieldPoC` 의 `roadSub > 1` 블록 «안»이고, `hfRoadSub` 는
  //   2026-08-27 에 1(off)로 폐기됐다 → 이 함수 자체가 기본 설정에서 **한 번도 안 불린다**.
  //   그래서 굽기 로더가 영영 안 깨어났다(실측: 도로 자산 네트워크 요청 0건).
  //   지금은 `HeightfieldPoC` 가 `setRoadBakeAnchor` 를 그 블록 «밖»에서 따로 부른다 —
  //   여기 남은 호출은 서브셀이 켜진 판에서의 중복이고, 같은 앵커면 즉시 물러나므로 공짜다.
  //   ★ 교훈: 얹을 자리를 고를 때 「그 함수가 존재하나」가 아니라 **「그 호출부가 지금도
  //     도나」**를 봐야 한다. 폴백이 있는 최적화는 죽어도 그림이 안 바뀐다.
  setRoadBakeAnchor(tx, tz);
  if (_anchor && _anchor[0] === tx && _anchor[1] === tz) return;
  _anchor = [tx, tz];
  scheduleRecompute();
};

const recompute = () => {
  const next = rasterize() || new Map();
  const nextSigs = new Map();
  let changed = false;
  for (const [tk, set] of next) {
    const s = sigOf(set);
    nextSigs.set(tk, s);
    if (_sigs.get(tk) !== s) changed = true;
  }
  if (!changed) for (const tk of _sigs.keys()) if (!nextSigs.has(tk)) { changed = true; break; }
  // 내용이 같은 타일은 sig 가 같다 → 그 타일은 재메시가 안 걸린다(옛 구조의 「전량 재메시」 소멸).
  if (!changed) return;
  _tiles = next;
  _sigs = nextSigs;
  _ver += 1;
  listeners.forEach((f) => { try { f(_ver); } catch { /* 구독자 실패는 무시 */ } });
};

// 밀집 구역(경복궁 등)에서 파티션이 짧은 간격으로 계속 들어오면 setRoadRings 가 연달아
// 새 참조를 받아, 호출마다 전체 래스터화를 다시 돌린다(2026-08-21, 모바일 "화면 깜빡임"
// 재현 로그로 확정). recompute 자체는 그대로 두고 **호출 빈도만** 죽인다 — 재빌드 키가
// 타일 sig 로 갈렸으므로 몇백ms 늦게 반영돼도 무해하다(다음 tick 이 알아서 잡는다).
//
// ★ 2026-08-22 — 200ms 디바운스는 무력이었다(호출이 200~400ms 간격이라 매번 만료).
//   최소 실행 간격을 **보장하는 스로틀**로 바꿨다: 이미 타이머가 잡혀 있으면 새로 안 깔고
//   (한 번의 실행이 그사이 들어온 모든 새 참조를 대표한다), 마지막 실행 이후 간격이 안 찼으면
//   남은 시간만큼만 미룬다. 어떤 빈도로 호출돼도 초당 최대 2회로 못박힌다.
let _recomputeTimer = null;
let _lastRecomputeAt = 0;
const RECOMPUTE_MIN_GAP_MS = 500;
const scheduleRecompute = () => {
  if (_recomputeTimer) return;   // 이미 예약돼 있다 — 그 실행이 지금 이 호출까지 대표한다
  const wait = Math.max(0, RECOMPUTE_MIN_GAP_MS - (performance.now() - _lastRecomputeAt));
  _recomputeTimer = setTimeout(() => {
    _recomputeTimer = null;
    _lastRecomputeAt = performance.now();
    recompute();
  }, wait);
};

/** 하이트필드 격자를 알려준다(HeightfieldPoC 가 meta 를 받은 뒤). 같은 격자면 무시. */
export const setRoadGrid = (meta) => {
  if (!meta || !(meta.cell > 0)) return;
  // 구운 도로의 지문 대조에 이 meta 가 필요하다(`h_src`·원점·격자). 로더가 heightfieldData 를
  // 못 여는 이유는 순환 때문이라, **이미 meta 를 받는 이 자리**가 값을 넘기는 문이다.
  setRoadBakeMeta(meta);
  if (_grid && _grid.cell === meta.cell && _grid.ox === meta.origin_x && _grid.oz === meta.origin_z) return;
  _grid = { cell: meta.cell, ox: meta.origin_x, oz: meta.origin_z };
  scheduleRecompute();
};

// ── ★ 도로 «등급» — 링과 같은 순서의 평행 배열 ──────────────────
//
// [왜 필요했나] 여태 이 모듈은 「도로인가 아닌가」만 답했다. 그런데 서울을 서울로 만드는 건
//   길이 «있다»가 아니라 **어떤 길이냐**다 — 한강대로와 골목이 같은 회색이면 도시가 안 읽히고,
//   레벨 동선의 척추(간선)도 화면에서 안 보인다. DB 는 처음부터 `road_type` 을 들고 있었고
//   (arterial 4,872 · collector 10,061 · service 8,986 · trail 11,144 · steps 1,246 · local 114),
//   **프론트가 링을 만들며 그걸 버리고 있었다.**
//
// [값] 0 = 도로 아님 · 1 = 간선(arterial) · 2 = 그 외 차도(collector/local/service)
//      · 3 = 보행로(trail/steps). 숫자인 이유는 조각 팔레트로 바로 꽂히기 때문이다.
// ⚠ 등급 배열을 «안 주면» 전부 2 로 본다 — 종전 동작(등급 없음)과 그림이 같아야 하기 때문이다.
export const ROAD_KIND = { NONE: 0, ARTERIAL: 1, ROAD: 2, PATH: 3 };
const TYPE_TO_KIND = {
  arterial: 1, collector: 2, local: 2, service: 2, alley: 2, trail: 3, steps: 3,
};
/** `world_road.road_type` 문자열 → 등급 숫자. 모르는 값은 「그 외 차도」로 본다. */
export const roadKindOfType = (t) => TYPE_TO_KIND[t] ?? 2;

let _kinds = [];          // _rings 와 **같은 순서·같은 길이**

/** 도로 링을 넣는다 — **월드 좌표**([x,z])여야 한다. 호출자가 g2x/g2z 로 변환해 넘긴다.
 *  @param {number[]} [kinds] 링과 같은 순서의 등급 배열(§ROAD_KIND). 없으면 전부 2.
 *  ⚠ 두 배열의 «순서»가 곧 짝이다. 링을 거르면 등급도 같은 자리에서 걸러야 한다 —
 *    아래에서 한 번에 거르는 이유가 그것이다(따로 거르면 조용히 한 칸씩 밀린다). */
export const setRoadRings = (rings, kinds = null) => {
  // 같은 배열이면 즉시 반환 — 호출자(HeightfieldPoC)의 useMemo 라 참조가 안정적이고,
  // 이 함수는 카메라가 셀을 넘을 때마다 불린다. 가드가 없으면 도로 246개를 매번 다시 래스터화한다.
  if (rings === _rawRings) return;
  _rawRings = rings;
  const src = Array.isArray(rings) ? rings : [];
  _rings = []; _kinds = [];
  for (let i = 0; i < src.length; i++) {
    const r = src[i];
    if (!Array.isArray(r) || r.length < 3) continue;
    _rings.push(r);
    _kinds.push(kinds && kinds[i] != null ? kinds[i] : 2);
  }
  // ── ★ 3차 — 「빠지는 링」으로는 무효화하지 않는다 ──────────────
  // [실측이 시킨 것] 링 수가 걸음 중 **815 ↔ 5,119 ↔ 1,002 ↔ 5,475** 로 출렁인다
  //   (`__roadKind.ring()`). 수천 개가 한꺼번에 들고 나므로 사각형 목록은 상한을 넘겨
  //   언제나 `null`(전량 폐기)로 떨어졌다 — 목록으로 바꾼 보람이 링 쪽엔 0 이었다.
  // [그런데 «빠지는» 것은 무효화 사유가 아니다] 링이 목록에서 빠지는 건 대개 **시야 밖으로
  //   나간 것**이지 도로가 없어진 게 아니다. 그 자리에 이미 만들어 둔 컬럼은 «여전히 맞다».
  //   나중에 그 자리를 다시 만들 때쯤이면 링이 도로 들어와 있고(그때 «붙는» 사건으로
  //   잡힌다), 그래서 스스로 낫는다.
  // [그래서] **붙은 링이 있을 때만** 도장을 올린다. 「길이가 달라졌다」가 아니라
  //   「없던 게 생겼다」가 판정이다 — `heightfieldData` §셋으로 가르기와 같은 규약이다.
  const _nextMap = ringMap(_rings);
  const _added = [];
  for (const [k, r] of _nextMap) if (!_prevRingSet.has(k)) _added.push(r);
  if (_added.length) {
    _epochRings = _rings.length;
    // ★ 「어느 자리가 달라졌나」를 같이 남긴다 — 들어온 링·나간 링의 테두리
    //   상자다. 종전엔 도장만 올렸고, 받는 두 지형 밭은 그걸 「밭 전체가 낡았다」로 읽어
    //   컬럼 1,200개를 통째로 버렸다. 도로 폴리곤 몇 개가 스트리밍된 것뿐인데.
    const _rr = rectsOfRings(_added);
    _ringDiag = { prev: _prevRingSet.size, next: _rings.length, added: _added.length,
                  got: _rr ? _rr.length : null };
    bumpKindEpoch(_rr, 'ring');
  }
  // ⚠ 도장이 «안» 올라간 호출에서도 스냅샷을 갱신한다 — 링 수가 같은데 내용만 바뀌는
  //   경우(하나 나가고 하나 들어옴)엔 도장이 안 오르므로, 안 갱신하면 다음 diff 가 낡은
  //   링까지 「나갔다」로 세어 상자가 쓸데없이 커진다. 안전측이긴 하나 공짜로 좁힐 수 있다.
  _prevRingSet = _nextMap;
  scheduleRecompute();
};

/** 들어오거나 나간 링만 골라 **하나의 테두리 상자**로 만든다. 가릴 수 없으면 `null`
 *  (= 「어딘지 모른다」 → 받는 쪽이 전량 폐기로 떨어진다. 그쪽이 언제나 안전측이다).
 *  ⚠ 합집합 상자 하나로 접는 이유 — 도로 링은 한 번에 수십 개가 같이 들고 나므로 목록으로
 *    들면 상한을 바로 넘긴다. 도로가 오는 자리는 카메라 둘레라 상자가 크게 안 번진다. */
// ── ★ 정정 — 「합집합 상자 하나」는 전량 폐기와 «같은 값»이었다 ──
// 1차는 들고 난 링을 통째로 접어 상자 **하나**로 냈다. 근거는 「목록으로 들면 상한을 바로
// 넘긴다」였는데, 실측이 그 근거를 뒤집었다: 사각형 폐기 2회에 **2,163컬럼**이 빠졌다
// (회당 ~1,081). 그때 밭 최대가 1,187컬럼이다 — **상자가 밭을 통째로 덮었다.** 즉
// `invalidate` 를 `dropRects(세계)` 로 «이름만» 바꾼 꼴이었다. 도로 링은 동 단위로 흩어져
// 있어 합집합은 언제나 그 사이 빈 땅까지 삼킨다 — `heightfieldData` §notify 가 「합집합으로
// 접지 말고 목록으로 들어라」라고 적어 둔 바로 그 함정이고, 도로에서 그대로 밟았다.
// ⚠ 상한을 넘기면 `null`(전량)이다.
//
// ── ★ 3차 정정 — 상한 64 때문에 이 수술이 «한 번도 발화하지 않았다» ──
// 2차까지의 근거는 「링 수백 개가 한꺼번에 바뀌는 건 «동 전환»이라 그때는 밭 전체가 낡은 게
// 맞다」였다. 실측이 그 전제를 통째로 뒤집었다:
//     ringDiag { prev: 1062, next: 5658, added: 4596, got: null }
//     roadDiag [ {src:'ring', n:0} × 4 ]        ← 사각형을 «한 번도» 못 냈다
// 링은 «동 전환»이 아니라 **평소 스트리밍**에서 수천 개씩 붙는다(도로 타일 반경이 넓어질
// 때마다). 그래서 `src:'ring'` 은 언제나 `null` 로 떨어졌고, 걸음 40초의 전량 폐기 3회가
// 전부 여기였다 — 목록으로 바꾼 보람이 링 쪽엔 **정확히 0** 이었다.
//
// [무엇이 상한을 강제했나] 상한의 근거는 「받는 쪽이 상자를 컬럼마다 다시 훑는다」였다.
//   그 비용이 O(상주 컬럼 × 상자)라 상자가 많으면 상자 훑기 자체가 프레임을 먹었다.
//   → `build/dirtyCols.js` 가 그 비용을 뒤집었다(상자가 많으면 «컬럼 집합»으로 편다).
//   비용 제약이 사라졌으니 상한도 같이 올린다. 답은 한 칸도 안 달라진다.
// ⚠ 그래도 상한은 남긴다 — 로그에 든 사각형이 무한정 쌓이면 그게 새 누수다(§로그 예산).
const RING_RECT_MAX = 8192;
// ── ★ 2차 정정 — 링은 «참조»로 못 가른다 ──────────
// 1차 정정에서 목록으로 바꿨는데도 값이 안 줄었다. 계측기(`__roadKind.diag()`)가 이유를
// 바로 냈다: `src:'ring'` 이 **매번 `n:0`**, 즉 언제나 `null`(전량)로 떨어지고 있었다.
// 링 배열이 호출마다 **새 객체로 다시 만들어지므로** `prev.has(ring)` 이 전부 거짓이고,
// 그러면 「전부 새 링」이 되어 상한 64를 넘겨 포기한다. 참조 동일성은 여기서 못 쓴다.
// → **내용 키**로 가른다: 길이 + 첫·가운데·끝 점. 다시 파싱해도 같은 값이 나온다.
//   충돌해도 손해는 「그 링의 무효화를 한 번 놓친다」뿐이고, 세 점을 다 쓰면 사실상 0 이다.
const ringKey = (r) => {
  const a = r[0], m = r[(r.length / 2) | 0], b = r[r.length - 1];
  return `${r.length}|${a[0]},${a[1]}|${m[0]},${m[1]}|${b[0]},${b[1]}`;
};

/** **붙은 링**을 링마다 하나씩 테두리 상자로. 못 가리면 `null`(= 전량, 안전측).
 *  @param {number[][][]} rings 이번에 새로 생긴 링만
 *  @returns {Array<{x0:number,z0:number,x1:number,z1:number}>|null} */
const rectsOfRings = (rings) => {
  const out = [];
  const eat = (ring) => {
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i];
      if (!p || p.length < 2) return false;
      if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
      if (p[1] < z0) z0 = p[1]; if (p[1] > z1) z1 = p[1];
    }
    if (!Number.isFinite(x0)) return false;
    out.push({ x0, z0, x1, z1 });
    return true;
  };
  for (const r of rings) {
    if (!eat(r) || out.length > RING_RECT_MAX) return null;   // 너무 많다 → 전량이 싸다
  }
  return out.length ? out : null;
};
/** 내용 키 → 링. `setRoadRings` 가 매 호출 갱신한다. */
const ringMap = (rings) => {
  const m = new Map();
  for (const r of rings) if (r.length) m.set(ringKey(r), r);
  return m;
};
let _prevRingSet = new Map();   // 내용 키 → 링 (참조가 아니다 — 위 §2차 정정)

// ── ★ 「`roadKindAt` 의 답이 달라졌을 수 있다」의 값싼 도장 ──────────
//
// [왜 `roadRingCount()` 로는 부족해졌나] 지형 컬럼은 「도로가 늦게 도착한다」를 링 «수»의
//   변화로 감지해 왔다(terrainChunkStore·terrainLodStore). 그런데 사전 굽기가 붙으면서
//   **답이 바뀌는 경로가 하나 더 생겼다** — 구운 타일이 뒤늦게 도착하면, 그 자리 컬럼은
//   폴리곤(아직 링이 없어 「길 없음」)으로 굳어 있다. 링 수는 그대로이므로 옛 도장으로는
//   그 순간이 안 잡힌다. 그래서 도장을 **둘을 합친 하나**로 올린다.
//
// ⚠ **재메시 «키»로 쓰지 마라** — 그게 「전량 재메시」의 원인이었다(위 §옛 구조).
//   무효화 «판정»에만 쓴다. 타일별 재빌드 키는 여전히 `roadSigForTile` 이다.
// ⚠ 구운 타일은 3×3 이 한꺼번에 도착한다. 도착마다 올리면 무효화(영역당 24ms)가 아홉 번
//   연달아 돈다 — 그래서 **한 번으로 모은다**(아래 300ms 코얼레싱).
let _kindEpoch = 0;
let _epochRings = -1;
let _bakeBumpTimer = null;
let _bakeRect = null;              // 코얼레싱 창 안에 도착한 타일 상자 **목록** (§6차)

// ── ★ 도장에 «자리»를 붙인다 ──────────────────────────────────────
// [무엇이 문제였나] 이 도장은 「이미 만든 컬럼이 낡았을 수 있다」만 말했다. 그래서 두 지형
//   밭은 값이 바뀔 때마다 **밭을 통째로 버렸다**(`terrainLodStore:invalidate('road')`).
//   실측: 전량 폐기 **7~16회**, 매번 컬럼 1,200개. 이 밭이 「커졌다
//   줄었다」 하는 원인의 «전부»가 여기였다 — 하이트필드 알림은 같은 구간에 **0회**였다.
// [고친 것] 도장을 올릴 때 «어디가» 달라졌는지 상자를 같이 로그에 남긴다. 소비자는
//   `roadKindRectsSince(내가 마지막에 본 도장)` 으로 그 사이 상자들을 받아 그 자리만 버린다.
// ⚠ 상자를 못 정하면 `null` 을 남긴다 → 소비자는 종전대로 전량 폐기다. **안전측이 기본**이다.
// ⚠ 로그는 유한하다(64). 오래 안 물어본 소비자는 로그 밖으로 밀려나므로 `null` 을 받는다 —
//   그것도 전량 폐기로 떨어진다. 「모르면 전부」 규약이 어디서도 안 깨진다.
const KIND_LOG_MAX = 64;
// ★ 로그 «사각형» 예산. 상한을 8,192 로 열었으므로 항목 수만 세면
//   로그가 수십만 개의 상자를 들 수 있다. 소비자는 매 `ensureAround` 마다 묻기 때문에
//   1~2 개정 이상 밀리는 일이 없다 — 오래된 항목을 밀어내도 잃는 게 없다.
//   밀려난 소비자는 `null`(전량 폐기)을 받는다. 「모르면 전부」가 여기서도 안 깨진다.
const KIND_RECT_BUDGET = 16384;
const _kindLog = [];               // [{ epoch, rects|null }]
let _kindRectN = 0;
const bumpKindEpoch = (rect, src = '?') => {
  _kindEpoch += 1;
  const list = Array.isArray(rect) ? rect : (rect ? [rect] : null);
  const rects = list && list.length ? list : null;
  _kindLog.push({ epoch: _kindEpoch, rects });
  _kindRectN += rects ? rects.length : 0;
  while (_kindLog.length > KIND_LOG_MAX
         || (_kindRectN > KIND_RECT_BUDGET && _kindLog.length > 1)) {
    const g = _kindLog.shift();
    _kindRectN -= g.rects ? g.rects.length : 0;
  }
  // 진단 — 사각형이 «얼마나 큰가». 세계만 한 상자면 사각형 폐기는 전량 폐기와 같은 값이다.
  //   그 경우 이 수술은 이름만 바뀐 것이므로, 값으로 확인할 자리가 있어야 한다.
  _kindDiag.push({ src, n: list ? list.length : 0,
                   w: list && list.length ? Math.round(Math.max(...list.map((r) => r.x1 - r.x0))) : null,
                   h: list && list.length ? Math.round(Math.max(...list.map((r) => r.z1 - r.z0))) : null });
  if (_kindDiag.length > 40) _kindDiag.shift();
};
const _kindDiag = [];
let _ringDiag = null;
if (typeof window !== 'undefined') window.__roadKind = { diag: () => _kindDiag.slice(), ring: () => _ringDiag, epoch: () => _kindEpoch };

// ★ 도착한 타일을 «합집합 상자 하나»로 접지 않는다. 종전엔 300ms 창에
//   들어온 타일을 하나로 접었고, 실측에서 그 상자가 **3072×2048m** 였다 — 그때 LOD 밭이
//   반경 1,120m(지름 2,240m)라 **상자가 밭을 통째로 덮었다.** 붙어 있는 3×3 이면 접어도
//   손해가 없지만, 떨어진 타일이 같은 창에 들어오면 그 사이 빈 땅까지 삼킨다.
//   목록으로 들면 그 경우가 사라진다 — 링 쪽이 4차에서 배운 것과 같은 함정이다.
onRoadBakeLand((rect) => {
  // `roadBake` 는 이제 **목록**을 준다(길이 있는 자리만). `null` = 「어딘지 모른다」 → 전량.
  const list = rect === null ? null : (Array.isArray(rect) ? rect : [rect]);
  if (list) {
    if (!_bakeRect) _bakeRect = [];
    for (const r of list) if (_bakeRect.length < RING_RECT_MAX) _bakeRect.push({ ...r });
  } else _bakeRect = _bakeRect || null;
  if (_bakeBumpTimer) return;      // 이미 예약돼 있다 — 그 한 번이 이 도착까지 대표한다
  const hadRect = () => _bakeRect;
  _bakeBumpTimer = setTimeout(() => {
    _bakeBumpTimer = null;
    // ⚠ 구운 타일은 3×3 이 한꺼번에 온다 — 300ms 창에 아홉 장이 목록으로 쌓인다.
    bumpKindEpoch(hadRect(), 'bake');
    _bakeRect = null;
  }, 300);
});
/** 링 도착 + 구운 타일 도착을 한 수로 센 도장. 값이 바뀌면 이미 만든 컬럼이 낡았을 수 있다. */
export const roadKindEpoch = () => _kindEpoch;

/** `?roadrect=0` — 도로 도장의 «자리» 규약을 끄고 **2026-08-30 이전 동작**(무조건 전량
 *  폐기)으로 되돌린다. A/B 계측의 유일한 정직한 방법이다. */
const ROAD_RECT = (() => {
  try { return new URLSearchParams(location.search).get('roadrect') !== '0'; } catch { return true; }
})();

/** `epoch` 이후로 «어디가» 달라졌나. 사각형 배열, 또는 **`null` = 모른다(전량 폐기)**.
 *  @param {number} epoch 소비자가 마지막으로 본 `roadKindEpoch()` 값 */
export const roadKindRectsSince = (epoch) => {
  if (!ROAD_RECT) return null;                      // 옛 동작 — 언제나 「모른다」
  if (!_kindLog.length) return null;
  if (epoch < _kindLog[0].epoch - 1) return null;   // 로그 밖으로 밀려났다 — 모른다
  const out = [];
  for (let i = 0; i < _kindLog.length; i++) {
    const e = _kindLog[i];
    if (e.epoch <= epoch) continue;
    if (!e.rects) return null;                      // 하나라도 「모른다」면 전부다
    for (const r of e.rects) out.push(r);
    if (out.length > RING_RECT_MAX * 2) return null;  // 너무 많이 밀렸다 — 전량이 싸다
  }
  return out;
};

/** 빌더에 넘길 스냅샷 — **그 타일 것만**. Set<string> 은 postMessage 구조화 복제 가능(워커).
 *  tx/tz 는 HeightfieldPoC 의 활성 타일 인덱스와 같은 값이다(둘 다 128셀 격자). */
export const roadCellsForTile = (tx, tz) => _tiles.get(`${tx}_${tz}`) || null;
/** **타일별 재빌드 키.** 이 타일의 도로 셀이 안 바뀌면 값이 안 바뀐다 → 재메시도 안 걸린다.
 *  옛 `roadCellsVersion()` 을 stepKeyBase 에 싣던 자리를 이것이 대신한다. */
export const roadSigForTile = (tx, tz) => _sigs.get(`${tx}_${tz}`) ?? 0;
/** 전역 버전 — 구독자 통지·소품 색 캐시용.
 *  ⚠ **지형 재빌드 키로 쓰지 마라.** 그게 「전량 재메시」의 원인이었다. */
export const roadCellsVersion = () => _ver;
/** 지금 들고 있는 도로 링 수 — 「도로가 도착했나 / 딴 동으로 넘어왔나」의 값싼 도장.
 *  ⚠ 이걸 **재메시 키로 쓰지 마라.** 무효화 «판정»에만 쓴다(전량 재메시가 그렇게 났다). */
export const roadRingCount = () => _rings.length;
export const roadCellCount = () => { let n = 0; for (const s of _tiles.values()) n += s.size; return n; };
export const onRoadCellsChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

/** 발밑(heightfieldData) 전용 — 이 월드 좌표의 셀이 도로 셀인가.
 *  렌더(hfBuild)와 **같은 격자·같은 Set** 을 봐야 발이 정확히 선다(SOP §5-C). */
export const roadCellAt = (meta, wx, wz) => {
  if (!_tiles.size || !_grid) return false;
  // 격자 폭이 다른 그룹이면 이 셀들과 같은 좌표계가 아니다 — 도로는 전역 하이트필드 격자
  // 하나에만 래스터화되므로 그런 그룹엔 애초에 도로 셀이 없다. 조용히 틀린 답을 내느니 막는다.
  if (meta.cell !== _grid.cell) return false;
  const p = worldToCellSub(meta, wx, wz);
  if (!p) return false;
  // 타일 인덱스는 **래스터화 때와 같은 기준**(_grid)으로 뽑아야 한다 — 발밑은 그룹마다 다른
  // meta 를 들고 오지만 전역 셀 인덱스(cgx)는 절대값이라 여기서 _grid 기준으로 되돌린다.
  const i = p.cgx - Math.round(_grid.ox / _grid.cell);
  const j = p.cgz - Math.round(_grid.oz / _grid.cell);
  const set = _tiles.get(tileKey(i, j));
  return set ? set.has(cellKey(p.cgx, p.cgz)) : false;
};

// ── 길 위 건축·굴착 금지 (049, 2026-08-01) ────────────────────────────────
// 서버(estate/service.py on_road)가 **폴리곤 정밀도**로 막는다. 고스트가 8m 셀(roadCellAt)로
// 미리보기를 그리면 서버보다 넓게 빨개져서 "빨간데 놓이는" 칸이 생긴다 — 그래서 여기도
// 같은 점-다각형 판정을 쓴다. 링은 setRoadRings 로 이미 들어와 있으니 데이터는 한 벌이다.
//
// 셀 Set 을 안 쓰고 따로 버킷을 두는 이유: 셀 Set 은 8m 격자에 묶여 있고(렌더용),
// 건축 격자는 1m 다. 정밀도가 다른 걸 억지로 공유하면 둘 다 틀어진다.
const RB = 32;   // m — 도로 폭이 1.2~12m 라 버킷당 폴리곤이 한 자릿수다
let _rbuckets = null;
let _rbucketsFor = null;

// ⚠ 버킷은 링이 아니라 **링 인덱스**를 담는다 — 등급(`_kinds`)을 같은 자리에서 꺼내야 하기 때문.
const buildRoadBuckets = () => {
  const m = new Map();
  for (let ri = 0; ri < _rings.length; ri++) {
    const ring = _rings[ri];
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (const [x, z] of ring) {
      if (x < x0) x0 = x; if (x > x1) x1 = x;
      if (z < z0) z0 = z; if (z > z1) z1 = z;
    }
    const bx0 = Math.floor(x0 / RB), bx1 = Math.floor(x1 / RB);
    const bz0 = Math.floor(z0 / RB), bz1 = Math.floor(z1 / RB);
    if (bx1 - bx0 > 400 || bz1 - bz0 > 400) continue;   // 이상 폴리곤
    for (let bx = bx0; bx <= bx1; bx += 1) {
      for (let bz = bz0; bz <= bz1; bz += 1) {
        const k = `${bx}_${bz}`;
        const arr = m.get(k);
        if (arr) arr.push(ri); else m.set(k, [ri]);
      }
    }
  }
  return m;
};

const pointInRing = (x, z, ring) => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, zi] = ring[i], [xj, zj] = ring[j];
    if ((zi > z) !== (zj > z)
        && x < ((xj - xi) * (z - zi)) / ((zj - zi) || 1e-12) + xi) inside = !inside;
  }
  return inside;
};

/** 이 월드 좌표가 도로 바닥 위인가 — 서버 estate_service.on_road 와 같은 판정. */
export const onRoad = (wx, wz) => roadKindAt(wx, wz) !== 0;

/** ★ 이 월드 좌표가 **어떤 등급의** 도로 위인가 (§ROAD_KIND). 도로가 아니면 0.
 *
 *  [왜 «가장 높은 등급»을 주나] 간선과 골목이 겹치는 교차로에서 골목이 이기면 큰길이
 *  거기서 끊겨 보인다. 화면에서 길은 **이어져 보이는 것이 전부**라, 겹치면 큰 쪽이 이긴다.
 *  (등급 숫자는 1=간선이 가장 «작다» — 그래서 최소값을 고른다.)
 *
 *  ⚠ 폴리곤 판정이라 8m 셀 격자보다 **훨씬 정밀하다**. 1m 복셀이 쓰는 게 이 쪽이다
 *    (`roadCellsForTile` 의 8m 셀은 하이트필드 서브분할용이라 눈금이 다르다). */
export const roadKindAt = (wx, wz) => {
  // ★ 사전 굽기 — **정수 격자 위에서만** 답한다. 그 자리에서는 아래 폴리곤 판정과
  //   비트 단위로 같은 값이다(굽는 쪽이 이 파일의 `roadMaskFill` 을 그대로 부른다).
  //   못 답하면 -1 이라 그대로 아래로 흐른다 — `?roadbake=0` 이면 항상 -1 이다.
  //   ⚠ 소수점 좌표를 구운 값으로 답하면 안 된다: `onRoad` 가 건축 금지 판정에 쓰이고
  //     서버는 폴리곤 정밀도로 막는다. 1m 격자로 반올림하면 길 가장자리에서 갈린다.
  const baked = roadBakeKindAt(wx, wz);
  if (baked >= 0) return baked;
  if (!_rings.length) return 0;
  if (_rbucketsFor !== _rawRings) { _rbuckets = buildRoadBuckets(); _rbucketsFor = _rawRings; }
  const arr = _rbuckets.get(`${Math.floor(wx / RB)}_${Math.floor(wz / RB)}`);
  if (!arr) return 0;
  let best = 0;
  for (const ri of arr) {
    if (!pointInRing(wx, wz, _rings[ri])) continue;
    const k = _kinds[ri] || 2;
    if (best === 0 || k < best) best = k;
    if (best === 1) return 1;                 // 간선이면 더 볼 것도 없다
  }
  return best;
};

// ── 창(窓) 하나의 1m 도로 마스크를 «한 번에» 채운다 ────
//
// [왜 따로 있나 — 실측] 도로 정지가 152×152 = 23,104칸짜리 창을 굽는데, 칸마다 위
//   `roadKindAt` 을 부르니 **창 하나에 51ms** 였다(실기 65장 3,303ms). 판정 자체가 느린 게
//   아니라 **버킷 키 문자열(`${bx}_${bz}`)을 칸마다 새로 만들어서**다 — 23,104번의 문자열
//   할당 + Map 조회. 여기서는 32m 버킷을 **한 번씩만** 열고 그 안의 링만 본다
//   (버킷 하나가 1,024칸이므로 조회가 1/1024 로 준다). 도로가 없는 버킷은 통째로 건너뛴다.
//
// ⚠ **판정은 `roadKindAt` 과 글자 그대로 같아야 한다** — 같은 `_rings`, 같은 `pointInRing`,
//   같은 「겹치면 큰 쪽(=작은 숫자)이 이긴다」. 여기가 갈리면 **칠한 자리와 깎은 자리가
//   어긋나** 길 옆에 포장 턱이 남는다. 그래서 새 판정식을 쓰지 않고 위 코드를 그대로 옮겼다.
/** @param {number} x0 창의 왼쪽 위 월드 X(정수 칸) @param {number} z0 같은 Z
 *  @param {number} W  창의 한 변(칸) @param {Uint8Array} out 길이 W*W. 여기에 등급을 채운다.
 *  @returns {boolean} 하나라도 도로가 있었나 */
export const roadMaskFill = (x0, z0, W, out) => {
  // ★ 사전 굽기 — 창에 걸치는 타일이 **전부** 와 있으면 폴리곤을 한 번도 안 돈다.
  //   하나라도 없으면 -1 이 와서 아래 종전 경로가 통째로 돈다(반반 섞지 않는다 — 그 이유는
  //   `roadBake.roadBakeFill` 머리주석 ⚠).
  const baked = roadBakeFill(x0, z0, W, out);
  if (baked >= 0) return baked === 1;
  out.fill(0);
  if (baked === -2) return false;   // 굽기가 깨졌다 — 이미 신고됐고, 폴리곤 판정으로 대신하지 않는다
  if (!_rings.length) return false;
  if (_rbucketsFor !== _rawRings) { _rbuckets = buildRoadBuckets(); _rbucketsFor = _rawRings; }
  // ── 창에 걸치는 링만 추린다 (버킷으로) ──────────────────────────────────────
  const bx0 = Math.floor(x0 / RB), bx1 = Math.floor((x0 + W - 1) / RB);
  const bz0 = Math.floor(z0 / RB), bz1 = Math.floor((z0 + W - 1) / RB);
  const hit = new Set();
  for (let bz = bz0; bz <= bz1; bz++) {
    for (let bx = bx0; bx <= bx1; bx++) {
      const arr = _rbuckets.get(`${bx}_${bz}`);
      if (arr) for (const ri of arr) hit.add(ri);
    }
  }
  if (!hit.size) return false;

  // ── 링마다 «줄»을 채운다 (scanline) ─────────────────────────────────────────
  // [왜 칸마다 점-다각형이 아닌가 — 실측] 칸마다 `pointInRing` 을 돌면 창 하나(152²)에
  //   ~23ms 다(`front/scripts/road_mask_bench.mjs`). 버킷을 미리 여는 것만으로는 1.14배
  //   밖에 안 줄었다 — **비용은 조회가 아니라 판정 자체**이고, 그건 칸수 × 링수 × 꼭짓점수다.
  //   줄 단위로 바꾸면 «줄수 × 꼭짓점수» + 「칸 채우기」로 떨어진다.
  //
  // ⚠ 판정이 위 `pointInRing` 과 **글자 그대로 같아야 한다.** 짝수-홀수 규칙도, 반열림
  //   z 규칙(`(zi > z) !== (zj > z)`)도, 부등호 방향(`x < xint`)도.
  //   · 반열림 z 규칙이라 **수평 변은 저절로 빠진다**(위 코드와 같다).
  //   · `x < xint` 이므로 「자기보다 큰 교점의 개수가 홀수면 안」이다. 교점을 오름차순으로
  //     늘어놓으면 안쪽 구간이 `[x₁, x₂) ∪ [x₃, x₄) …` 가 된다 → 정수 칸은
  //     `ceil(x₁) … ceil(x₂)-1`.
  //   이 동치를 «말»로 두지 않고 `front/scripts/test_road_mask_parity.mjs` 가 값으로 못박는다.
  const xs = [];
  let any = false;
  for (const ri of hit) {
    const ring = _rings[ri];
    const kind = _kinds[ri] || 2;
    let rz0 = Infinity, rz1 = -Infinity;
    for (let i = 0; i < ring.length; i++) { const z = ring[i][1]; if (z < rz0) rz0 = z; if (z > rz1) rz1 = z; }
    const j0 = Math.max(z0, Math.ceil(rz0 - 1)), j1 = Math.min(z0 + W - 1, Math.floor(rz1 + 1));
    for (let wz = j0; wz <= j1; wz++) {
      xs.length = 0;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
        const zi = ring[i][1], zj = ring[j][1];
        if ((zi > wz) === (zj > wz)) continue;         // 반열림 — 수평 변도 여기서 빠진다
        const xi = ring[i][0], xj = ring[j][0];
        xs.push(((xj - xi) * (wz - zi)) / ((zj - zi) || 1e-12) + xi);
      }
      if (xs.length < 2) continue;
      xs.sort((a, b) => a - b);
      const row = (wz - z0) * W - x0;
      for (let s = 0; s + 1 < xs.length; s += 2) {
        let a = Math.ceil(xs[s]), b = Math.ceil(xs[s + 1]) - 1;
        if (a < x0) a = x0;
        if (b > x0 + W - 1) b = x0 + W - 1;
        for (let wx = a; wx <= b; wx++) {
          const p = row + wx;
          if (out[p] === 0 || kind < out[p]) out[p] = kind;   // 겹치면 «큰 길»(작은 숫자)이 이긴다
        }
        if (b >= a) any = true;
      }
    }
  }
  return any;
};

export default { setRoadGrid, setRoadRings, setRoadAnchor, roadCellsForTile, roadSigForTile, roadCellsVersion, roadCellAt, onRoad, roadKindAt, roadKindEpoch, roadKindRectsSince, roadKindOfType, roadMaskFill, ROAD_KIND };
